import { infiniteQueryOptions } from '@tanstack/react-query';
import type { Message, Paginated } from '@rundown/types';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';

/**
 * The conversation on a request.
 *
 *   GET  /workspaces/:ws/requests/:id/messages?limit&cursor -> Paginated<Message>
 *   POST /workspaces/:ws/requests/:id/messages  { body, internalOnly? }
 *
 * What comes back is already filtered by the server: a client role never
 * receives an internal note, so there is nothing to hide here. `visibleToClient`
 * is present on the rows Noorix receives purely so the UI can *mark* them.
 *
 * ── WHICH WAY A PAGE RUNS ─────────────────────────────────────────────────────
 * `items` is always oldest-first, because that is how a thread reads. The PAGES
 * run the other way: page 0 is the newest messages, page 1 the ones before
 * them, and so on backwards. `nextCursor` therefore means "older than this".
 *
 * That is why every consumer has to reverse the page list before flattening —
 * see the Conversation component. Getting it wrong does not throw; it silently
 * renders the conversation in blocks of shuffled time, which is precisely the
 * kind of wrongness a reader blames on themselves.
 */

function messagesPath(workspaceId: string, requestId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/messages`;
}

/** The server's own default. Its ceiling is 200. */
const MESSAGES_PAGE_SIZE = 100;

export function fetchMessages(
  workspaceId: string,
  requestId: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<Paginated<Message>> {
  const params = new URLSearchParams({ limit: String(MESSAGES_PAGE_SIZE) });
  if (cursor !== null) params.set('cursor', cursor);
  return api.get<Paginated<Message>>(`${messagesPath(workspaceId, requestId)}?${params.toString()}`, signal);
}

export interface NewMessageInput {
  /**
   * A Tiptap/ProseMirror document — exactly what `Composer.getContent()`
   * returns. `unknown` here, not a shared type: the API validates this
   * against its own allowlisted node/mark grammar
   * (`apps/api/.../message-body.ts`) and is the single source of truth for
   * what shape is actually accepted, so nothing on this side should claim to
   * know the shape more precisely than that.
   */
  readonly body: unknown;
  /** Noorix only; ignored by the server for client roles. */
  readonly internalOnly?: boolean;
  /** Already uploaded and registered; linked to the message as it is written. */
  readonly attachmentIds?: readonly string[];
}

export function postMessage(
  workspaceId: string,
  requestId: string,
  input: NewMessageInput,
): Promise<Message> {
  return api.post<Message>(messagesPath(workspaceId, requestId), input);
}

/**
 * The thread, paged backwards from the newest.
 *
 * The key is unchanged, so the realtime feed's `message.*` invalidation and the
 * post mutation's own still match it. Invalidating an infinite query refetches
 * the pages already held rather than collapsing back to the first, so a reply
 * arriving while somebody is reading an hour-old part of the thread does not
 * throw away the earlier messages they loaded to get there.
 */
export function messagesInfiniteQueryOptions(workspaceId: string, requestId: string) {
  return infiniteQueryOptions({
    queryKey: queryKeys.requestMessages(workspaceId, requestId),
    queryFn: ({ pageParam, signal }) => fetchMessages(workspaceId, requestId, pageParam, signal),
    // Null asks for the tail of the conversation, which is what opening a
    // thread means.
    initialPageParam: null as string | null,
    // Null is the server's "that was the beginning of the thread", and it is
    // also how TanStack Query reads "no next page", so `hasNextPage` goes false
    // on its own once the first message has been reached.
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function editMessage(
  workspaceId: string,
  requestId: string,
  messageId: string,
  body: unknown,
): Promise<Message> {
  return api.patch<Message>(`${messagesPath(workspaceId, requestId)}/${encodeURIComponent(messageId)}`, {
    body,
  });
}

export function deleteMessage(workspaceId: string, requestId: string, messageId: string): Promise<void> {
  return api.delete<void>(`${messagesPath(workspaceId, requestId)}/${encodeURIComponent(messageId)}`);
}
